import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { InAppBrowser } from '@capgo/inappbrowser';

export type CameraBrowserExitReason = 'return-url' | 'dismissed';

export interface CameraBrowserExit {
  reason: CameraBrowserExitReason;
  url?: string;
}

export interface CameraBrowserOptions {
  onExit?: (event: CameraBrowserExit) => void | Promise<void>;
}

@Injectable()
export class InAppBrowserUtils {
  private cameraBrowserListeners: PluginListenerHandle[] = [];
  private cameraBrowserSession = 0;

  constructor() {}

  openUrl(url: string): void {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url });
    } else {
      this.openUrlOnWeb(url);
    }
  }

  /**
   * Opens a URL in a native WebView that can use the app's camera permission.
   * Keep regular links on openUrl(), which uses the system in-app browser.
   */
  async openCameraUrl(url: string, options: CameraBrowserOptions = {}): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.openUrlOnWeb(url);
      return;
    }

    await this.clearCameraBrowserListeners();
    const session = ++this.cameraBrowserSession;
    let exited = false;

    const exit = async (event: CameraBrowserExit): Promise<void> => {
      if (exited || session !== this.cameraBrowserSession) {
        return;
      }
      exited = true;
      await this.clearCameraBrowserListeners();

      if (event.reason === 'return-url') {
        // Stop the app return URL before the native WebView renders the web app.
        try {
          await InAppBrowser.close();
        } catch {
          // The user may have dismissed it at the same time.
        }
      }

      await options.onExit?.(event);
    };

    const urlChangeListener = await InAppBrowser.addListener('urlChangeEvent', ({ url: nextUrl }) => {
      if (this.isAppReturnUrl(url, nextUrl)) {
        void exit({ reason: 'return-url', url: nextUrl });
      }
    });
    const closeListener = await InAppBrowser.addListener('closeEvent', ({ url: closedUrl }) => {
      void exit({ reason: 'dismissed', url: closedUrl });
    });

    if (session !== this.cameraBrowserSession) {
      await Promise.all([urlChangeListener.remove(), closeListener.remove()]);
      return;
    }

    this.cameraBrowserListeners = [urlChangeListener, closeListener];
    await InAppBrowser.openWebView({
      url,
      // The plugin cancels these main-frame navigations before they render and
      // emits urlChangeEvent, which lets the native app take over immediately.
      blockedHosts: ['tidy.com', '*.tidy.com', 'gotidy.com', '*.gotidy.com'],
    });
  }

  /**
   * Closes the in-app browser if it is currently open (native only).
   * Use this to return the user to the app after an external flow
   * (e.g. identity verification) completes, instead of leaving the
   * external/redirect web page visible.
   */
  async closeUrl(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      ++this.cameraBrowserSession;
      await this.clearCameraBrowserListeners();

      try {
        await InAppBrowser.close();
      } catch {
        // Camera-capable WebView may already be closed; ignore.
      }

      try {
        await Browser.close();
      } catch {
        // Browser may already be closed; ignore.
      }
    }
  }

  private async clearCameraBrowserListeners(): Promise<void> {
    const listeners = this.cameraBrowserListeners;
    this.cameraBrowserListeners = [];
    await Promise.all(listeners.map(async listener => {
      try {
        await listener.remove();
      } catch {
        // Listener may already have been removed during a simultaneous close.
      }
    }));
  }

  private isAppReturnUrl(initialUrl: string, nextUrl: string): boolean {
    if (!nextUrl || nextUrl === initialUrl) {
      return false;
    }

    try {
      const initial = new URL(initialUrl);
      const next = new URL(nextUrl);

      if (next.protocol !== 'http:' && next.protocol !== 'https:') {
        return true;
      }

      const nextHost = next.hostname.toLowerCase();
      const isTidyHost = nextHost === 'tidy.com'
        || nextHost.endsWith('.tidy.com')
        || nextHost === 'gotidy.com'
        || nextHost.endsWith('.gotidy.com');
      if (isTidyHost) {
        return true;
      }

      // Didit keeps its verification pages on didit.me. Treat a top-level
      // navigation away from Didit as the verification provider handing back.
      const initialIsDidit = initial.hostname === 'didit.me' || initial.hostname.endsWith('.didit.me');
      const nextIsDidit = nextHost === 'didit.me' || nextHost.endsWith('.didit.me');
      return initialIsDidit && !nextIsDidit;
    } catch {
      return false;
    }
  }

  private openUrlOnWeb(url: string): void {
    // Check if running on iOS Safari
    const isIOSSafari =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isIOSSafari) {
      // For iOS Safari, use location.href instead of window.open to avoid popup blocker
      window.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
